package top.wcpe.allincore.ui.runtime.ir.eval

import top.wcpe.allincore.dsl.ir.expr.ExprNode
import top.wcpe.allincore.dsl.ir.expr.StmtNode
import top.wcpe.allincore.ui.runtime.internal.concurrent.newConcurrentSet
import top.wcpe.allincore.ui.runtime.internal.log.UiLog
import top.wcpe.allincore.ui.runtime.ir.DslRuntimeError
import top.wcpe.allincore.ui.runtime.ir.DslValueUtils
import top.wcpe.allincore.ui.runtime.ir.IrRange
import top.wcpe.allincore.ui.runtime.ir.api.ClientApi
import top.wcpe.allincore.ui.runtime.ir.api.ItemView
import top.wcpe.allincore.ui.runtime.ir.api.LocalApi
import top.wcpe.allincore.ui.runtime.ir.api.OptionsApi
import top.wcpe.allincore.ui.runtime.ir.api.PlaceholderApi
import top.wcpe.allincore.ui.runtime.ir.api.PlaceholderEntry
import top.wcpe.allincore.ui.runtime.ir.api.WorldApi
import top.wcpe.allincore.ui.runtime.ir.call.BoundFunRefValue
import top.wcpe.allincore.ui.runtime.ir.call.CallDispatcher
import top.wcpe.allincore.ui.runtime.ir.call.FunRefValue
import top.wcpe.allincore.ui.runtime.ir.call.LocalFunValue
import top.wcpe.allincore.ui.runtime.ir.request.RequestExecutor
import top.wcpe.allincore.ui.runtime.ir.state.StateMachineExecutor
import top.wcpe.allincore.ui.runtime.ir.state.StateTypeGuard
import top.wcpe.allincore.ui.runtime.reactive.BatchScope
import top.wcpe.allincore.ui.runtime.reactive.Computed
import top.wcpe.allincore.ui.runtime.reactive.ReactiveWrapper
import top.wcpe.allincore.ui.runtime.reactive.Signal
import kotlin.jvm.JvmField
import kotlin.jvm.JvmStatic
import kotlin.math.floor

/**
 * 表达式求值器：在给定 [EvalContext] 上对 [ExprNode] 求值并执行 [StmtNode]。
 * 读取 [Signal.get] 时会自动登记响应式依赖（详见 MutableSignal），因此把 [eval] 放进 Effect/Computed 即可获得自动重算。
 */
@Suppress("TooManyFunctions")
object ExprEvaluator {

    private val LOGGER = UiLog("top.wcpe.allincore.ui.runtime.ir.eval.ExprEvaluator")

    /** 已警告过的未知 theme key 集合，用于按 key 去重 warning（避免 Effect 重算刷屏）。 */
    private val warnedKeys: MutableSet<String> = newConcurrentSet()

    /** 测试辅助：清空已 warn 的 theme key 集合，避免用例间互相干扰。 */
    @JvmStatic
    fun resetThemeWarnings() {
        warnedKeys.clear()
    }

    // ===== 推荐入口（基于 EvalContext） =====

    /** 求值表达式，返回值类型为 String / Double / Boolean / Map / null。 */
    @JvmStatic
    @Suppress("CyclomaticComplexMethod", "LongMethod", "NestedBlockDepth", "ReturnCount", "ThrowsCount")
    fun eval(node: ExprNode, ctx: EvalContext): Any? {
        when (node) {
            is ExprNode.Literal -> return node.value
            is ExprNode.StateRef -> {
                val sig = ctx.states[node.name]
                // 未声明 state：返回 null，由调用方 stringify/算术降级，避免整树构建失败
                return sig?.get()
            }
            is ExprNode.ParamsRef -> return ctx.params[node.name]
            // session.x：从当前 EvalContext 注入的共享状态域读取
            is ExprNode.SessionRef -> return ctx.sharedStateAccess.sessionSignal(node.name, null).get()
            // persist.x：从当前 EvalContext 注入的应用持久域读取
            is ExprNode.PersistRef -> return ctx.sharedStateAccess.persistSignal(node.name, null).get()
            is ExprNode.DerivedRef -> {
                // derived.x：从派生值表取对应 Computed 并 .get()，触发懒求值与依赖追踪。
                // 未声明（理论上被编译期 E201 拦截）时返回 null，保持运行期宽松。
                val c: Computed<Any?>? = ctx.derived[node.name]
                return c?.get()
            }
            is ExprNode.IdentRef -> {
                val idName = node.name
                // Phase 1：统一查找表替代 11 个 if-else 分支（local/vanilla/slot/papi/time/date/timer/
                // debug/ui/session/persist/client 等内建标识符统一由 ctx.builtin 查表返回）
                val builtinApi = ctx.builtin(idName)
                if (builtinApi != null) return builtinApi
                // 回调参数 scope；找不到时返回 null（保持宽松）。Signal 自动解包：$for 循环变量以
                // Signal<Object> 形式存入 locals，调用 .get() 触发响应式依赖追踪。
                val local = ctx.locals[idName]
                return if (local is Signal<*>) local.get() else local
            }
            is ExprNode.LocalRef -> {
                val varName = node.name
                if (!ctx.hasVar(varName)) {
                    throw DslRuntimeError("未声明的局部变量: $varName")
                }
                return ctx.getVar(varName)
            }
            is ExprNode.NullLit -> return null
            is ExprNode.UnaryOp -> {
                val v = eval(node.operand, ctx)
                when (node.op) {
                    "-" -> {
                        if (v is Number) return -v.toDouble()
                        throw DslRuntimeError("一元 '-' 操作数必须为数值，实际: ${DslValueUtils.classOf(v)}")
                    }
                    "!" -> return !DslValueUtils.truthy(v)
                    else -> throw DslRuntimeError("未知一元运算符: ${node.op}")
                }
            }
            is ExprNode.SafeMemberAccess -> {
                val target = eval(node.target, ctx) ?: return null
                if (target is ItemView) return target.readField(node.member)
                if (target is Map<*, *>) return target[node.member]
                return null
            }
            is ExprNode.SafeMemberCall -> {
                val target = eval(node.target, ctx) ?: return null
                return CallDispatcher.invokeMemberCall(target, node.method, node.args, ctx)
            }
            is ExprNode.Elvis -> {
                val l = eval(node.left, ctx)
                return l ?: eval(node.right, ctx)
            }
            is ExprNode.Ternary -> {
                val c = eval(node.cond, ctx)
                return if (DslValueUtils.truthy(c)) eval(node.thenExpr, ctx) else eval(node.elseExpr, ctx)
            }
            is ExprNode.IfExpr -> {
                return if (DslValueUtils.truthy(eval(node.cond, ctx))) {
                    evalBranchBody(node.thenBody, ctx)
                } else {
                    evalBranchBody(node.elseBody, ctx)
                }
            }
            is ExprNode.MemberAccess -> return evalMemberAccess(node, ctx)
            is ExprNode.MemberCall -> return CallDispatcher.evalMemberCall(node, ctx)
            is ExprNode.ThemeRef -> {
                val key = node.key
                val lookup = ctx.themeLookup
                if (lookup.contains(key)) {
                    return lookup.get(key)
                }
                // 未命中：按 key 去重 warn，返回 null 让上层降级
                if (warnedKeys.add(key)) {
                    LOGGER.warn("未定义的 theme token: $key（返回 null）")
                }
                return null
            }
            is ExprNode.Interp -> {
                val sb = StringBuilder()
                for (p in node.parts) {
                    when (p) {
                        is ExprNode.Interp.TextPart -> sb.append(p.text)
                        is ExprNode.Interp.ExprPart -> sb.append(DslValueUtils.stringify(eval(p.expr, ctx)))
                    }
                }
                return sb.toString()
            }
            is ExprNode.BinaryOp -> {
                // 短路逻辑：&& / ||
                when (node.op) {
                    "&&" -> {
                        val l = eval(node.left, ctx)
                        if (!DslValueUtils.truthy(l)) return false
                        return DslValueUtils.truthy(eval(node.right, ctx))
                    }
                    "||" -> {
                        val l = eval(node.left, ctx)
                        if (DslValueUtils.truthy(l)) return true
                        return DslValueUtils.truthy(eval(node.right, ctx))
                    }
                    else -> return applyBinary(node.op, eval(node.left, ctx), eval(node.right, ctx))
                }
            }
            is ExprNode.ListLiteral -> {
                val out = ArrayList<Any?>(node.elements.size)
                for (e in node.elements) {
                    out.add(eval(e, ctx))
                }
                return out
            }
            is ExprNode.MapLiteral -> {
                // M6-a：Map 字面量求值为 LinkedHashMap，保留声明顺序
                val out = LinkedHashMap<String, Any?>()
                for (entry in node.entries) {
                    out[entry.key] = eval(entry.value, ctx)
                }
                return out
            }
            is ExprNode.Range -> {
                val s = eval(node.start, ctx)
                val e = eval(node.endValue, ctx)
                if (s !is Number || e !is Number) {
                    throw DslRuntimeError(
                        "range 两端必须为数值，实际: ${DslValueUtils.classOf(s)} , ${DslValueUtils.classOf(e)}",
                    )
                }
                val sd = s.toDouble()
                val ed = e.toDouble()
                return if (node.kind == ExprNode.RangeKind.CLOSED) IrRange.closed(sd, ed) else IrRange.halfOpen(sd, ed)
            }
            is ExprNode.Index -> {
                val target = eval(node.target, ctx)
                val idx = eval(node.index, ctx)
                return indexInto(target, idx)
            }
            is ExprNode.When -> return evalWhen(node, ctx)
            is ExprNode.Lambda -> return CallDispatcher.makeLambdaValue(node, ctx)
            is ExprNode.FunRef -> return evalFunRef(node, ctx)
            is ExprNode.CallExpr -> return CallDispatcher.evalCallExpr(node, ctx)
            is ExprNode.BuiltinCall -> return CallDispatcher.evalBuiltinCall(node, ctx)
            is ExprNode.IsExpr -> {
                val v = eval(node.target, ctx)
                return checkIsType(v, node.typeName)
            }
            // Stage B：net.subscribe("ns.topic", handler) 求值——独立 IR 节点而非 BuiltinCall，
            // 与 net.request (StmtNode.Request) 同级"协议级内置调用"分类，不走 CallDispatcher.evalBuiltinCall。
            is ExprNode.NetSubscribe -> return CallDispatcher.evalNetSubscribe(node, ctx)
        }
        throw DslRuntimeError("未知 ExprNode: ${node::class.simpleName}")
    }

    /** MemberAccess 节点求值：内建作用域属性 / Map / ItemView / List.size 等成员访问。 */
    @Suppress("CyclomaticComplexMethod", "ReturnCount", "NestedBlockDepth")
    private fun evalMemberAccess(node: ExprNode.MemberAccess, ctx: EvalContext): Any? {
        val placeholderEntry = placeholderEntryTarget(node.target, ctx)
        if (placeholderEntry != null) {
            if ("value" == node.member) return placeholderEntry.currentValue()
            if ("available" == node.member) return placeholderEntry.currentAvailable()
        }
        val target = eval(node.target, ctx)
        // v4 §E.3：client.world → 返回 WorldApi 子作用域对象（供后续 .posX / .entities(r) 求值）
        if (target is ClientApi && "world" == node.member) return target.world()
        // Phase 2：client.options → 返回 OptionsApi（Signal Map 模式，后续 .fov 走 Map.get 自动 unwrap）
        if (target is ClientApi && "options" == node.member) return target.options()
        // Phase 2：OptionsApi 属性访问 → 从 entries() Map 中取值（Signal 自动 unwrap）
        if (target is OptionsApi) {
            val entries = target.entries()
            val entryValue = entries[node.member]
            return if (entryValue is Signal<*>) entryValue.get() else entryValue
        }
        // Phase 2：client.input → 返回 InputApi（命令方法模式）
        if (target is ClientApi && "input" == node.member) return target.input()
        // client.isSnapshot → 返回当前是否快照版（对齐原版 Minecraft.isSnapshot()）
        if (target is ClientApi && "isSnapshot" == node.member) return target.isSnapshot()
        // v4 §E.3：client.world.posX / posY / posZ / yaw / dimension 属性访问
        if (target is WorldApi) {
            return when (node.member) {
                "posX" -> target.posX()
                "posY" -> target.posY()
                "posZ" -> target.posZ()
                "yaw" -> target.yaw()
                "dimension" -> target.dimension()
                // tiles / entities 是方法调用（MemberCall），不应走属性访问；其它未知成员返回 null 保持宽松
                else -> null
            }
        }
        // local.player → 返回 LocalApi 的 player Signal 表
        if (target is LocalApi && "player" == node.member) return target.player()
        // local.boss → 返回 LocalApi 的 boss Signal 表（自定义 boss 血条 HUD 使用）
        if (target is LocalApi && "boss" == node.member) return target.boss()
        // local.viewport → 返回 LocalApi 的 viewport Signal 表（width/height/rootOffsetX/Y）
        if (target is LocalApi && "viewport" == node.member) return target.viewport()
        if (target is PlaceholderApi) return target.entry(node.member).currentValue()
        if (target is PlaceholderEntry) {
            if ("value" == node.member) return target.currentValue()
            if ("available" == node.member) return target.currentAvailable()
            return null
        }
        if (target is ItemView) return target.readField(node.member)
        if (target is Map<*, *>) {
            val mapValue = target[node.member]
            // 响应式解包：如果 map value 是 Signal，自动 .get() 触发 reactive tracking
            return if (mapValue is Signal<*>) mapValue.get() else mapValue
        }
        // List 属性访问：.size 返回元素数量（与 List.size() 方法调用等价）
        if (target is List<*> && "size" == node.member) return target.size.toDouble()
        return null
    }

    /** FunRef 求值：无绑定实参返回 FunRefValue，否则按现场 ctx 立即求值 snapshot 绑定实参返回 BoundFunRefValue。 */
    private fun evalFunRef(node: ExprNode.FunRef, ctx: EvalContext): Any {
        if (node.boundArgs.isEmpty() && node.boundNamedArgs.isEmpty()) {
            return FunRefValue(node.name)
        }
        // M6-e：snapshot 绑定实参（按现场 ctx 立即求值），返回 BoundFunRefValue。
        val boundArgs = ArrayList<Any?>(node.boundArgs.size)
        for (a in node.boundArgs) boundArgs.add(eval(a, ctx))
        val boundNamed = LinkedHashMap<String, Any?>()
        for (na in node.boundNamedArgs) {
            boundNamed[na.name] = eval(na.value, ctx)
        }
        return BoundFunRefValue(node.name, boundArgs, boundNamed)
    }

    /**
     * M5-c：实现 `value is TypeName` 的运行时类型判断。
     * 仅支持基础内置类型名；未知类型名按 false 处理（不抛错以保持 runtime 健壮）。
     */
    private fun checkIsType(v: Any?, typeName: String): Boolean = when (typeName) {
        "Number", "Int", "Long", "Double" -> v is Number
        "String" -> v is String
        "Boolean", "Bool" -> v is Boolean
        "Map" -> v is Map<*, *>
        "List" -> v is List<*>
        "Any" -> v != null
        "Unit" -> v == null
        else -> false
    }

    private fun placeholderEntryTarget(target: ExprNode, ctx: EvalContext): PlaceholderEntry? {
        if (target !is ExprNode.MemberAccess) return null
        val root = eval(target.target, ctx)
        if (root is PlaceholderApi) {
            return root.entry(target.member)
        }
        return null
    }

    /** 列表 / Map 索引读；越界 / 非数值索引按运行期异常或宽松返回。 */
    @Suppress("ThrowsCount")
    private fun indexInto(target: Any?, idx: Any?): Any? {
        // M6-a：Map 索引读 —— 字符串键查找，缺失返回 null（不抛错）。
        if (target is Map<*, *>) {
            return target[mapKey(idx)]
        }
        if (target is List<*>) {
            if (idx !is Number) {
                throw DslRuntimeError("列表索引必须为整数，实际: ${DslValueUtils.classOf(idx)}")
            }
            val dv = idx.toDouble()
            if (dv.isNaN() || dv.isInfinite() || dv != floor(dv)) {
                throw DslRuntimeError("列表索引必须为整数，实际: $dv")
            }
            val i = dv.toLong().toInt()
            if (i < 0 || i >= target.size) {
                throw DslRuntimeError("列表索引越界: $i（size=${target.size}）")
            }
            return target[i]
        }
        if (target == null) {
            throw DslRuntimeError("不能对 null 进行索引访问")
        }
        throw DslRuntimeError("不支持索引访问的目标类型: ${DslValueUtils.classOf(target)}")
    }

    /** Map 索引键：字符串原样使用；其它类型走 stringify 兜底，避免对 Number/Boolean 直接抛错。 */
    private fun mapKey(idx: Any?): String {
        if (idx is String) return idx
        if (idx == null) {
            throw DslRuntimeError("Map 索引键不能为 null")
        }
        return DslValueUtils.stringify(idx)
    }

    /**
     * M6-a：执行 `target[idx] = value` 索引赋值。
     * - target 是 Map：索引按字符串 key 写入；缺失 key 直接插入
     * - target 是 List：索引必须为整数，越界抛错
     * - 其它类型 / null：抛 DslRuntimeError
     */
    @Suppress("UNCHECKED_CAST", "ThrowsCount")
    private fun indexAssign(target: Any?, idx: Any?, value: Any?) {
        if (target is Map<*, *>) {
            (target as MutableMap<String, Any?>)[mapKey(idx)] = value
            return
        }
        if (target is List<*>) {
            val list = target as MutableList<Any?>
            if (idx !is Number) {
                throw DslRuntimeError("列表索引必须为整数，实际: ${DslValueUtils.classOf(idx)}")
            }
            val dv = idx.toDouble()
            if (dv.isNaN() || dv.isInfinite() || dv != floor(dv)) {
                throw DslRuntimeError("列表索引必须为整数，实际: $dv")
            }
            val i = dv.toLong().toInt()
            if (i < 0 || i >= list.size) {
                throw DslRuntimeError("列表索引越界: $i（size=${list.size}）")
            }
            list[i] = value
            return
        }
        if (target == null) {
            throw DslRuntimeError("不能对 null 进行索引赋值")
        }
        throw DslRuntimeError("不支持索引赋值的目标类型: ${DslValueUtils.classOf(target)}")
    }

    /** 表达式位置的 when：分支体最后表达式作为返回值；无匹配且无 else 时返回 null。 */
    private fun evalWhen(w: ExprNode.When, ctx: EvalContext): Any? {
        val subject = eval(w.subject, ctx)
        for (br in w.branches) {
            for (v in br.values) {
                val vv = eval(v, ctx)
                if (DslValueUtils.equalsValue(subject, vv)) {
                    return evalBranchBody(br.body, ctx)
                }
            }
        }
        if (w.hasElse()) {
            // hasElse() 已保证 elseBody 非 null；跨模块无法 smart-cast 公开属性，用局部 val + !! 复刻旧平台类型非空传递。
            return evalBranchBody(w.elseBody!!, ctx)
        }
        return null
    }

    /** 执行分支体，最后一条若为 ExprStmt 则返回其值；否则返回 null。 */
    @JvmStatic
    fun evalBranchBody(body: List<StmtNode>, ctx: EvalContext): Any? {
        if (body.isEmpty()) return null
        val last = body.size - 1
        for (i in 0 until last) {
            executeStmt(body[i], ctx)
        }
        val tail = body[last]
        if (tail is StmtNode.ExprStmt) {
            return eval(tail.call, ctx)
        }
        executeStmt(tail, ctx)
        return null
    }

    /**
     * 执行 action body：依次执行各语句。
     *
     * 顶层 wrap：捕获 [ReturnSignal] 表示 action / callback 体的 return；其余 break/continue 信号若泄漏到顶层
     * 视为编译期未拦住，按内部错误处理。同时为本次调用 push 一个独立的 var 帧，保证同一 action / callback 多次触发
     * 时局部变量互不污染。自动合批：execute 的入口 / 出口包一层 [BatchScope]，让本次执行体内多次响应式写入合并为一次订阅者通知。
     */
    @JvmStatic
    fun execute(stmts: List<StmtNode>, ctx: EvalContext) =
        execute(stmts, ctx, emptyMap())

    /**
     * 执行 action body，并在 push 的 var 帧上预注入 [capturedVars]。
     *
     * 用于 net.request 旧 callback-body 形态：回调异步执行时外层 var 帧已 pop，
     * 把发起 request 时 snapshot 的外层局部变量注入回调的新帧，让回调内能引用外层 var，
     * 与 DSL 语法上"回调看起来像闭包 lambda"的直觉一致。
     */
    @JvmStatic
    fun execute(stmts: List<StmtNode>, ctx: EvalContext, capturedVars: Map<String, Any?>) {
        BatchScope.enter()
        ctx.pushVarFrame()
        try {
            if (capturedVars.isNotEmpty()) {
                for ((k, v) in capturedVars) ctx.declareVar(k, v)
            }
            executeBlock(stmts, ctx)
        } catch (_: ReturnSignal) {
            // 正常 return：在 callable 体边界吞掉
        } catch (s: BreakSignal) {
            throw DslRuntimeError("break/continue 越出循环边界（编译期未拦住）", s)
        } catch (s: ContinueSignal) {
            throw DslRuntimeError("break/continue 越出循环边界（编译期未拦住）", s)
        } finally {
            ctx.popVarFrame()
            BatchScope.exit()
        }
    }

    /**
     * 执行 action body 并返回 return 值（若 body 有 return 表达式）。
     * 用于拦截器（interceptClick 等）这类需要从 DSL 取同步 bool 的场景：与 [execute] 一致地处理 var 帧 /
     * return 信号 / 自动合批，但显式返回 ReturnSignal 携带的值；body 未走 return 分支时返回 null。
     */
    @JvmStatic
    fun executeAndReturn(stmts: List<StmtNode>, ctx: EvalContext): Any? {
        BatchScope.enter()
        ctx.pushVarFrame()
        try {
            executeBlock(stmts, ctx)
            return null
        } catch (rs: ReturnSignal) {
            return rs.value
        } catch (s: BreakSignal) {
            throw DslRuntimeError("break/continue 越出循环边界（编译期未拦住）", s)
        } catch (s: ContinueSignal) {
            throw DslRuntimeError("break/continue 越出循环边界（编译期未拦住）", s)
        } finally {
            ctx.popVarFrame()
            BatchScope.exit()
        }
    }

    /**
     * 执行 stmt 列表，传播控制流信号。
     * 调用方应在循环 / callable 边界处自行捕获 [BreakSignal] / [ContinueSignal] / [ReturnSignal]。
     */
    private fun executeBlock(stmts: List<StmtNode>, ctx: EvalContext) {
        for (s in stmts) {
            executeStmt(s, ctx)
        }
    }

    /**
     * D2-b 入口：在调用方已经准备好的 var 帧上直接执行语句序列，不再额外 push/pop。
     * 用于 [StateMachineExecutor]：状态机已经把 record values 注入了一层"record 帧"，内部 VarDecl 必须直接落在该帧上。
     * ReturnSignal / Break / Continue 信号原样穿透。自动合批：每次进入此入口同样开启 batch，并在出口 flush。
     */
    @JvmStatic
    fun executeOnCurrentFrame(stmts: List<StmtNode>, ctx: EvalContext) {
        BatchScope.enter()
        try {
            executeBlock(stmts, ctx)
        } finally {
            BatchScope.exit()
        }
    }

    @Suppress("CyclomaticComplexMethod", "LongMethod", "ThrowsCount")
    private fun executeStmt(s: StmtNode, ctx: EvalContext) {
        when (s) {
            is StmtNode.Assign -> {
                val sig = ctx.states[s.stateName] ?: throw errorAt(s, "未声明的 state: ${s.stateName}")
                val value = eval(s.rhs, ctx)
                StateTypeGuard.checkAssignable(s.stateName, ctx.stateTypes[s.stateName], value, s.line, s.column)
                // 路线 B：赋给 state 的 List/Map 递归包成反应式集合，使 state.x.add(y) 等 mutate 自动触发订阅者重跑。
                sig.set(ReactiveWrapper.wrap(value))
            }
            is StmtNode.SessionAssign -> {
                // session.x = rhs：写到当前 EvalContext 注入的 session 域。
                val value = ReactiveWrapper.wrap(eval(s.rhs, ctx))
                ctx.sharedStateAccess.sessionSignal(s.fieldName, value).set(value)
            }
            is StmtNode.PersistAssign -> {
                // persist.x = rhs：写到当前 EvalContext 注入的应用持久域。
                val value = ReactiveWrapper.wrap(eval(s.rhs, ctx))
                ctx.sharedStateAccess.persistSignal(s.fieldName, value).set(value)
            }
            is StmtNode.LocalAssign -> {
                if (!ctx.hasVar(s.name)) {
                    throw errorAt(s, "赋值到未声明的局部变量: ${s.name}")
                }
                ctx.setVar(s.name, eval(s.rhs, ctx))
            }
            is StmtNode.IndexAssign -> {
                // M6-a：target[index] = rhs；List/Map 分别处理
                val target = eval(s.target, ctx)
                val idx = eval(s.index, ctx)
                val value = eval(s.rhs, ctx)
                indexAssign(target, idx, value)
            }
            is StmtNode.VarDecl -> ctx.declareVar(s.name, eval(s.initializer, ctx))
            is StmtNode.If -> {
                if (DslValueUtils.truthy(eval(s.cond, ctx))) {
                    executeBlock(s.thenBody, ctx)
                } else {
                    executeBlock(s.elseBody, ctx)
                }
            }
            is StmtNode.While -> executeWhile(s, ctx)
            is StmtNode.Break -> throw BreakSignal
            is StmtNode.Continue -> throw ContinueSignal
            is StmtNode.Return -> {
                // 跨模块无法对公开属性 smart-cast，取局部 val 固定后判空。
                val retExpr = s.value
                val v = if (retExpr == null) null else eval(retExpr, ctx)
                throw ReturnSignal(v)
            }
            is StmtNode.Try -> executeTry(s, ctx)
            is StmtNode.Batch -> {
                // batch { stmts }：在 BatchScope enter/exit 之间执行块内语句，使响应式写入合并为一次订阅者通知。
                BatchScope.enter()
                try {
                    executeBlock(s.body, ctx)
                } finally {
                    BatchScope.exit()
                }
            }
            is StmtNode.ForIn -> executeForIn(s, ctx)
            // 语句形态：丢弃返回值
            is StmtNode.WhenStmt -> evalWhen(s.getWhen(), ctx)
            is StmtNode.Request -> RequestExecutor.runRequest(s, ctx)
            // D2-b：类型化状态机入口；ReturnSignal 由外层 execute() 统一吞掉。
            is StmtNode.StateMachine -> StateMachineExecutor.executeStateMachine(s, ctx)
            is StmtNode.LocalFunDecl ->
                // 局部 fun 声明：创建 LocalFunValue 写入当前栈顶帧。按引用闭包语义：不持有 captured，调用时直接复用当前帧栈。
                ctx.declareLocalFun(s.name, LocalFunValue(s.name, s.params, s.body, s.defaults))
            // 表达式语句：求值后丢弃返回值（典型用法是 local.openScreen("x")）
            is StmtNode.ExprStmt -> eval(s.call, ctx)
            else -> throw errorAt(s, "未知 StmtNode: ${s::class.simpleName}")
        }
    }

    /** for-in：每轮把元素写入帧顶 NAME；break/continue 在循环内捕获，return 穿透。 */
    private fun executeForIn(f: StmtNode.ForIn, ctx: EvalContext) {
        val iterObj = eval(f.iterable, ctx)
        if (iterObj !is Iterable<*>) {
            throw errorAt(f, "for-in 的右侧必须可迭代，实际: ${DslValueUtils.classOf(iterObj)}")
        }
        ctx.pushVarFrame()
        try {
            for (item in iterObj) {
                ctx.declareVar(f.varName, item)
                try {
                    executeBlock(f.body, ctx)
                } catch (_: BreakSignal) {
                    return
                } catch (_: ContinueSignal) {
                    // 进入下一轮
                }
            }
        } finally {
            ctx.popVarFrame()
        }
    }

    /** 单独抽出 while 以便清晰地处理 break/continue。 */
    private fun executeWhile(w: StmtNode.While, ctx: EvalContext) {
        while (DslValueUtils.truthy(eval(w.cond, ctx))) {
            try {
                executeBlock(w.body, ctx)
            } catch (_: BreakSignal) {
                return
            } catch (_: ContinueSignal) {
                // 进入下一轮条件判断
            }
        }
    }

    /**
     * try/catch/finally 执行；仅捕获 [DslRuntimeError]，控制流信号穿透。
     * - try 体内抛 [DslRuntimeError] → 进入 catch 体；catch 体内的异常 / 控制流信号由内层 try 单独捕获并暂存到 pending。
     * - try / catch 体内抛 [BreakSignal]/[ContinueSignal]/[ReturnSignal] → 暂存到 pending 走 finally 再上抛。
     * - finally 体内若再抛新异常会覆盖前面的 pending，与 Java 语义一致。
     */
    @Suppress("ReturnCount")
    private fun executeTry(t: StmtNode.Try, ctx: EvalContext) {
        var pending: Throwable? = null
        try {
            executeBlock(t.tryBody, ctx)
        } catch (err: DslRuntimeError) {
            // 进入 catch 体；用独立的 try 包住，避免外层再次走 catch 路径
            try {
                ctx.declareVar(t.catchName, err)
                executeBlock(t.catchBody, ctx)
            } catch (sig: BreakSignal) {
                pending = sig
            } catch (sig: ContinueSignal) {
                pending = sig
            } catch (sig: ReturnSignal) {
                pending = sig
            } catch (reErr: DslRuntimeError) {
                pending = reErr
            }
        } catch (sig: BreakSignal) {
            pending = sig
        } catch (sig: ContinueSignal) {
            pending = sig
        } catch (sig: ReturnSignal) {
            pending = sig
        }
        if (t.finallyBody.isNotEmpty()) {
            executeBlock(t.finallyBody, ctx)
        }
        if (pending is RuntimeException) throw pending
        if (pending != null) throw DslRuntimeError("意外异常类型", t.line, t.column, pending)
    }

    // ===== 兼容包装：保留旧签名，转调新接口 =====

    @Deprecated("使用 eval(ExprNode, EvalContext)", ReplaceWith("eval(node, EvalContext.of(states))"))
    @JvmStatic
    fun eval(node: ExprNode, states: Map<String, Signal<Any?>>): Any? = eval(node, EvalContext.of(states))

    @Deprecated("使用 execute(List, EvalContext)", ReplaceWith("execute(stmts, EvalContext.of(states))"))
    @JvmStatic
    fun execute(stmts: List<StmtNode>, states: Map<String, Signal<Any?>>) = execute(stmts, EvalContext.of(states))

    // ===== 内部工具 =====

    /** 构造附带 stmt 源码位置的运行时错误。 */
    private fun errorAt(s: StmtNode, msg: String): DslRuntimeError = DslRuntimeError(msg, s.line, s.column)

    /** 二元运算分派已迁出至 BinaryOpEvaluator；保留私有 wrapper 是为了 eval 内调用点保持简洁。 */
    private fun applyBinary(op: String, l: Any?, r: Any?): Any? = BinaryOpEvaluator.applyBinary(op, l, r)

    // ===== 控制流 sentinel（不上抛 stacktrace，节省成本） =====

    /** break 哨兵；singleton（栈迹仅单例构造时填一次；common 无 writableStackTrace 四参构造）。仅同模块捕获。 */
    internal object BreakSignal : RuntimeException()

    /** continue 哨兵；singleton（同上）。仅同模块捕获。 */
    internal object ContinueSignal : RuntimeException()

    /** return 哨兵：携带返回值；public，便于跨包 CallDispatcher / StateMachineExecutor / RequestCallbacks 捕获。 */
    class ReturnSignal(@JvmField val value: Any?) : RuntimeException()
}
